// Zoom Modal Functionality
class ZoomModal {
  constructor() {
    this.currentZoom = 1;
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.translateX = 0;
    this.translateY = 0;
    this.modal = null;
    this.modalImage = null;
    this.isModalOpen = false;

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () =>
        this.setupEventListeners()
      );
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    // Check if imagery grid is enabled
    const imageryGrid = document.querySelector(
      ".carousel-thumbnails.imagery-grid"
    );
    if (!imageryGrid) return;

    const gridThumbnails = document.querySelectorAll(
      ".carousel-thumbnails.imagery-grid .thumbnail"
    );

    // Add double-click to grid thumbnails
    gridThumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener("dblclick", (e) => {
        e.preventDefault();
        this.openModal(
          thumbnail.getAttribute("data-src"),
          thumbnail.getAttribute("alt"),
          thumbnail.getAttribute("data-media-type")
        );
      });
    });

    // Setup main image click listener (single click for zoom)
    this.setupMainImageListener();

    // Setup modal event listeners
    this.setupModalListeners();
  }

  setupMainImageListener() {
    const mainMediaContainer = document.getElementById("mainMediaContainer");
    if (!mainMediaContainer) return;

    // Add double-click listener to main media container
    mainMediaContainer.addEventListener("dblclick", (e) => {
      const mainImage = mainMediaContainer.querySelector("img");
      const mainVideo = mainMediaContainer.querySelector("video");

      if (mainImage && e.target === mainImage) {
        e.preventDefault();
        this.openModal(mainImage.src, mainImage.alt, "image");
      } else if (mainVideo && e.target === mainVideo) {
        e.preventDefault();
        this.openModal(mainVideo.src, mainVideo.alt || "Video", "video");
      }
    });
  }

  setupModalListeners() {
    this.modal = document.getElementById("zoomModal");
    this.modalImage = document.getElementById("zoomModalImage");

    if (!this.modal || !this.modalImage) return;

    // Close modal when clicking overlay
    const overlay = this.modal.querySelector(".zoom-modal-overlay");
    if (overlay) {
      overlay.addEventListener("click", (e) => {
        e.stopPropagation();
        this.closeModal();
      });
    }

    // Close modal when clicking close button
    const closeBtn = this.modal.querySelector(".zoom-modal-close");
    if (closeBtn) {
      closeBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.closeModal();
      });
    }

    // Close modal when clicking outside the image container
    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal || e.target === overlay) {
        this.closeModal();
      }
    });

    // Mouse wheel zoom (only for images)
    this.modalImage.addEventListener("wheel", (e) => {
      e.preventDefault();
      if (e.deltaY < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    });

    // Click to zoom functionality (only for images)
    this.modalImage.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Toggle between zoom levels
      if (this.currentZoom === 1) {
        this.zoomIn();
      } else if (this.currentZoom < 2) {
        this.zoomIn();
      } else {
        this.resetZoom();
      }
    });

    // Keyboard shortcuts
    document.addEventListener("keydown", (e) => {
      if (!this.isModalOpen) return;

      switch (e.key) {
        case "Escape":
          this.closeModal();
          break;
        case "+":
        case "=":
          // Only zoom for images
          if (this.modalImage.style.display !== "none") {
            e.preventDefault();
            this.zoomIn();
          }
          break;
        case "-":
          // Only zoom for images
          if (this.modalImage.style.display !== "none") {
            e.preventDefault();
            this.zoomOut();
          }
          break;
        case "0":
          // Only zoom for images
          if (this.modalImage.style.display !== "none") {
            e.preventDefault();
            this.resetZoom();
          }
          break;
      }
    });
  }

  openModal(mediaSrc, mediaAlt, mediaType = "image") {
    if (!this.modal || !this.modalImage) return;

    // Clear existing content
    this.modalImage.style.display = "none";

    // Remove existing video if any
    const existingVideo = this.modal.querySelector("video");
    if (existingVideo) {
      existingVideo.remove();
    }

    if (mediaType === "video") {
      // Create video element for modal
      const video = document.createElement("video");
      video.src = mediaSrc;
      video.controls = true;
      video.autoplay = false;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.style.width = "100%";
      video.style.height = "auto";
      video.style.maxHeight = "80vh";
      video.style.objectFit = "contain";

      // Insert video before the image
      this.modalImage.parentNode.insertBefore(video, this.modalImage);
    } else {
      // Show image
      this.modalImage.src = mediaSrc;
      this.modalImage.alt = mediaAlt;
      this.modalImage.style.display = "block";
    }

    this.modal.style.display = "block";
    document.body.style.overflow = "hidden";
    this.isModalOpen = true;

    // Reset zoom only for images
    if (mediaType === "image") {
      this.currentZoom = 1;
      this.updateImageTransform();
    }
  }

  closeModal() {
    if (!this.modal) return;

    // Pause and remove any video
    const existingVideo = this.modal.querySelector("video");
    if (existingVideo) {
      existingVideo.pause();
      existingVideo.remove();
    }

    // Reset image display
    this.modalImage.style.display = "block";

    this.modal.style.display = "none";
    document.body.style.overflow = "";
    this.isModalOpen = false;
  }

  zoomIn() {
    this.currentZoom = Math.min(this.currentZoom * 1.2, 5);
    this.updateImageTransform();
  }

  zoomOut() {
    this.currentZoom = Math.max(this.currentZoom / 1.2, 0.5);
    this.updateImageTransform();
  }

  resetZoom() {
    this.currentZoom = 1;
    this.updateImageTransform();
  }

  updateImageTransform() {
    if (!this.modalImage) return;
    this.modalImage.style.transform = `scale(${this.currentZoom})`;
  }
}

// Initialize zoom modal when DOM is ready
document.addEventListener("DOMContentLoaded", function () {
  new ZoomModal();
});
